import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';

export interface AccordionItem {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  image: string;
}

const GAP = 20;
const DURATION = 800;

export default function PerspectivesAccordion({ items }: { items: AccordionItem[] }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [active, setActive] = useState(0);
  const [shown, setShown] = useState(0);
  const [settled, setSettled] = useState(true);
  const [exp, setExp] = useState(0);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      const n = items.length;
      setExp(((w - GAP * (n - 1)) * 2) / (n + 1));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [items.length]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const activate = (i: number) => {
    if (i === active) return;
    window.clearTimeout(timer.current);
    setSettled(false);
    setActive(i);
    timer.current = window.setTimeout(() => {
      setShown(i);
      setSettled(true);
    }, DURATION + 20);
  };

  return (
    <div
      ref={rowRef}
      onMouseLeave={() => activate(0)}
      className="flex flex-col gap-5 lg:h-[560px] lg:flex-row"
      style={{ '--exp': `${exp}px` } as CSSProperties}
    >
      {items.map((a, i) => (
        <Link
          key={a.slug}
          to={`/insights/${a.slug}`}
          onMouseEnter={() => activate(i)}
          onFocus={() => activate(i)}
          aria-label={a.title}
          className="group relative block h-[420px] cursor-pointer overflow-hidden bg-background-200 outline-none focus-visible:ring-2 focus-visible:ring-primary-500 lg:h-auto lg:min-w-0 lg:basis-0 lg:[contain:paint] lg:[flex-grow:var(--g)] lg:[transition:flex-grow_800ms_cubic-bezier(0.65,0,0.35,1)] lg:[will-change:flex-grow]"
          style={{ '--g': active === i ? 2 : 1 } as CSSProperties}
        >
          <img
            src={a.image}
            alt={`${a.title} — ${a.category} perspective by AVENOR`}
            title={a.title}
            loading="lazy"
            decoding="async"
            className="editorial-img absolute inset-0 h-full w-full object-cover object-top lg:left-1/2 lg:right-auto lg:w-[var(--exp)] lg:max-w-none lg:-translate-x-1/2"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/65 via-foreground-950/10 to-transparent" />

          <div
            className={`absolute inset-x-0 bottom-0 p-7 text-background-50 transition-opacity md:p-9 ${
              settled ? 'lg:opacity-100 lg:duration-500' : 'lg:opacity-0 lg:duration-150'
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-label text-[11px] uppercase tracking-[0.14em] text-background-50/70">
              <span className="text-[13px] tracking-normal">{String(i + 1).padStart(2, '0')}</span>
              <span className={`h-1.5 w-1.5 rounded-full ${i === 0 ? 'bg-primary-500' : 'bg-background-50/70'}`} />
              <span className="whitespace-nowrap">{a.category}</span>
              <span className="whitespace-nowrap">· {a.date}</span>
            </div>
            <h3 className="mt-4 max-w-md font-heading text-2xl font-light leading-snug md:text-[28px]">{a.title}</h3>
            <p className={`mt-4 max-w-sm text-[15px] leading-[1.7] text-background-50/80 ${shown === i ? '' : 'lg:hidden'}`}>
              {a.excerpt}
            </p>
            <span className={`mt-6 items-center gap-2 whitespace-nowrap font-label text-[11px] uppercase tracking-[0.14em] group-hover:text-primary-300 ${shown === i ? 'inline-flex' : 'inline-flex lg:hidden'}`}>
              <i className="ri-arrow-right-line" /> Read article
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}